import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { KstDataGrid } from '@/components/KstDataGrid';
import { ConfirmModal } from '@/components/ConfirmModal';
import { destroyReactComponent } from '@/react-bridge';
import { NotesFilters } from '@/components/screens/notes/NotesFilters';
import { NotesAuditLogTab } from '@/components/screens/notes/NotesAuditLogTab';
import type { NotesAuditLogTabHandle } from '@/components/screens/notes/NotesAuditLogTab';
import { NoteFormModal } from '@/components/screens/notes/NoteFormModal';

import type {
  KstDataGridColumn,
  KstDataGridPager,
  KstDataGridRowAction,
  KstDataGridRows,
} from '@/components/KstDataGrid';

import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import type { AjaxFailureInfo } from '@/services/api-class';
import { recordNoteWriteFailure, flushNoteWriteTelemetry } from '@/services/note-write-telemetry';
import moment from 'moment';
import { currentUserHasPermission } from '@/utils/legacy-utils';
import { AccessAuthorities } from '@/models';
import { Codes } from '@/constants/codes';

// ============================================================================
// External Declarations
// ============================================================================

import { uploadBlockBlob } from '@/services/azure-blob-service';
import { load } from '@/services/loading';

// Lazy wrappers: these window globals are set by MyBuiltScripts.js which loads
// after ReactBundle.js. Wrapping them defers resolution to call time.
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const deepCopyProperties = _wg('deepCopyProperties');
const getAttachmentInfoList = _wg('getAttachmentInfoList');
const getObjectInArrayByProperty = _wg('getObjectInArrayByProperty');
const openWebpage = _wg('openWebpage');

// ============================================================================
// Module-level State
// ============================================================================

let notesContainer: HTMLElement | null = null;
let notesPageRefreshInterval: ReturnType<typeof setInterval> | null = null;
let refreshCallback: (() => void) | null = null;

// ============================================================================
// Types
// ============================================================================

interface NotesFilters {
  subject: string;
  location: string;
  notes: string;
}

interface NoteListItem {
  id: number;
  displayId: number;
  recipientType: string;
  recipientTypeDisplay: string;
  npsdClient: number | null;
  npsdClientName: string;
  selectedClientSites: any[];
  sitesDisplay: string;
  dateTime: Date | string;
  loggedBy: number;
  loggedByName: string;
  onBehalfOf: number | null;
  onBehalfOfName: string;
  location: string;
  subject: string;
  notes: string;
  attachmentList: any[];
  resizedImageList: any[];
  resizedImageUrl: string;
}

interface EditingNote {
  id: number | null;
  displayId: number | null;
  recipientType: string;
  npsdClient: number | null;
  selectedSiteIds: number[];
  dateTime: Date;
  onBehalfOf: number | null;
  location: string;
  subject: string;
  notes: string;
  attachmentList: any[];
  resizedImageList: any[];
  attachmentInfoList: any[];
}

// ============================================================================
// Helpers
// ============================================================================

function createEmptyEditingNote(): EditingNote {
  return {
    id: null,
    displayId: null,
    recipientType: '',
    npsdClient: null,
    selectedSiteIds: [],
    dateTime: new Date(),
    onBehalfOf: null,
    location: '',
    subject: '',
    notes: '',
    attachmentList: [],
    resizedImageList: [],
    attachmentInfoList: [],
  };
}

function getNotesGridColumns(): KstDataGridColumn[] {
  const columns: KstDataGridColumn[] = [
    { field: 'displayId', name: 'ID', size: '50px' },
    { field: 'recipientTypeDisplay', name: 'Recipients' },
    {
      field: 'npsdClientName',
      name: 'Client',
      template: (e: any) => e.npsdClientName || '\u2014',
    },
    {
      field: 'sitesDisplay',
      name: 'Sites',
      template: (e: any) => e.sitesDisplay || '\u2014',
    },
    {
      field: 'dateTime',
      name: 'Date/Time',
      template: (e: any) =>
        e.dateTime ? moment(e.dateTime).format('MM/DD/YYYY HH:mm') : '',
    },
    { field: 'loggedByName', name: 'Logged By' },
    {
      field: 'onBehalfOfName',
      name: 'On Behalf',
      template: (e: any) => e.onBehalfOfName || '\u2014',
    },
    {
      field: 'location',
      name: 'Location',
      template: (e: any) => e.location || '\u2014',
    },
    { field: 'subject', name: 'Subject' },
    { field: 'notes', name: 'Notes', fullWidth: true },
  ];

  // Image column
  columns.push({
    field: 'resizedImageUrl',
    name: 'Image',
    size: '80px',
    template: (e: any) => {
      if (e.resizedImageUrl) {
        return `<img src="${e.resizedImageUrl}" class="note-thumbnail" alt="Note image" style="max-width: 60px; max-height: 40px; cursor: pointer;" onclick="event.stopPropagation(); if(window.notesVueInstance) window.notesVueInstance.openImage(${JSON.stringify(e).replace(/"/g, '&quot;')});">`;
      }
      return '\u2014';
    },
  });

  return columns;
}

function getNotesRowActions(
  canModify: boolean,
  canDelete: boolean
): KstDataGridRowAction[] {
  const actions: KstDataGridRowAction[] = [];
  if (canModify) {
    actions.push({
      name: 'Edit',
      icon: 'fa-pencil',
      event: 'edit',
      iconOnly: true,
    });
  }
  if (canDelete) {
    actions.push({
      name: 'Delete',
      icon: 'fa-trash',
      event: 'delete',
      class: 'btn-danger',
      iconOnly: true,
    });
  }
  return actions;
}

// ============================================================================
// NotesScreen Component
// ============================================================================

export function NotesScreen() {
  const { theme } = useTheme();

  // --- Permissions ---
  const canCreateNote = useMemo(
    () => currentUserHasPermission(AccessAuthorities.ROLE_ORNA_NOTES_SAVE),
    []
  );
  const canModifyNote = useMemo(
    () => currentUserHasPermission(AccessAuthorities.ROLE_ORNA_NOTES_UPDATE),
    []
  );
  const canDeleteNote = useMemo(
    () => currentUserHasPermission(AccessAuthorities.ROLE_ORNA_NOTES_DELETE),
    []
  );
  const canSetOnBehalfOf = useMemo(
    () =>
      currentUserHasPermission(
        AccessAuthorities.ROLE_ORNA_NOTES_ON_BEHALF_OF
      ),
    []
  );

  // --- Tab State ---
  const [activeTab, setActiveTab] = useState<'notes' | 'auditLog'>('notes');

  // --- Notes Grid State ---
  const [isLoading, setIsLoading] = useState(false);
  const [notes, setNotes] = useState<NoteListItem[]>([]);
  const [notesGridRows, setNotesGridRows] = useState<KstDataGridRows>({
    entries: null,
  });
  const [notesGridPager, setNotesGridPager] = useState<KstDataGridPager>({
    curPage: 1,
    numPages: 1,
    records: 0,
    isVisible: false,
  });
  const [filters, setFilters] = useState<NotesFilters>({
    subject: '',
    location: '',
    notes: '',
  });
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // --- Note Modal State ---
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [isNewNote, setIsNewNote] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [editingNote, setEditingNote] = useState<EditingNote>(
    createEmptyEditingNote()
  );
  const [isDateTimePickerOpen, setIsDateTimePickerOpen] = useState(false);

  // --- Delete Modal State ---
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<NoteListItem | null>(null);
  const [isDeletingNote, setIsDeletingNote] = useState<boolean>(false);

  // --- Dropdown Data ---
  const [recipientTypes, setRecipientTypes] = useState<
    Array<{ code: string; value: string }>
  >([]);
  const [clients, setClients] = useState<Array<{ id: number; name: string }>>(
    []
  );
  const [clientSites, setClientSites] = useState<
    Array<{ id: number; siteName: string }>
  >([]);
  const [onBehalfOfUsers, setOnBehalfOfUsers] = useState<
    Array<{ id: number; name: string }>
  >([]);

  // --- Refs ---
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const editingNoteRef = useRef(editingNote);
  editingNoteRef.current = editingNote;
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;
  const auditLogRef = useRef<NotesAuditLogTabHandle>(null);

  // --- Column Definitions (memoized) ---
  const notesGridColumns = useMemo(() => getNotesGridColumns(), []);
  const notesRowActions = useMemo(
    () => getNotesRowActions(canModifyNote, canDeleteNote),
    [canModifyNote, canDeleteNote]
  );
  // --- Computed Values ---
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.subject) count++;
    if (filters.location) count++;
    if (filters.notes) count++;
    return count;
  }, [filters]);

  const recipientTypeItems = useMemo(
    () =>
      recipientTypes.map((rt) => ({
        label: rt.value,
        value: rt.code,
      })),
    [recipientTypes]
  );

  const clientItems = useMemo(
    () =>
      clients.map((c) => ({
        label: c.name,
        value: c.id,
      })),
    [clients]
  );

  const onBehalfOfItems = useMemo(() => {
    const items: Array<{ label: string; value: number | null }> = [
      { label: 'None', value: null },
    ];
    onBehalfOfUsers.forEach((u) => {
      items.push({ label: u.name, value: u.id });
    });
    return items;
  }, [onBehalfOfUsers]);

  const deleteConfirmMessage = useMemo(() => {
    const noteId = noteToDelete ? noteToDelete.displayId : '';
    return `<p>Are you sure you want to delete note <strong>#${noteId}</strong>?</p><p>This action cannot be undone.</p>`;
  }, [noteToDelete]);

  // ===== Load Functions =====

  const loadNotes = useCallback(
    (pageOverride?: number) => {
      // Clear previous results before fetching new data
      setNotes([]);
      setNotesGridRows({ entries: [] });
      setNotesGridPager({ curPage: 1, numPages: 1, records: 0, isVisible: false });
      setIsLoading(true);
      const page = pageOverride !== undefined ? pageOverride : currentPageRef.current;
      const currentFilters = filtersRef.current;

      const apiFilters: any[] = [];
      if (currentFilters.subject) {
        apiFilters.push({
          field: 'subject',
          operator: 'contains',
          value: currentFilters.subject,
        });
      }
      if (currentFilters.location) {
        apiFilters.push({
          field: 'location',
          operator: 'contains',
          value: currentFilters.location,
        });
      }
      if (currentFilters.notes) {
        apiFilters.push({
          field: 'notes',
          operator: 'contains',
          value: currentFilters.notes,
        });
      }

      const operation = {
        data: {
          page: page,
          take: pageSize,
          skip: (page - 1) * pageSize,
          filter:
            apiFilters.length > 0
              ? { logic: 'and', filters: apiFilters }
              : null,
        },
      };

      api.notesSearch(
        page,
        String(pageSize),
        JSON.stringify(operation),
        (result: any) => {
          const newTotalCount = result.records || 0;
          const newTotalPages = Math.ceil(newTotalCount / pageSize) || 1;
          const notesList = result.notes || [];

          const mappedNotes: NoteListItem[] = notesList.map((note: any) => {
            const recipientTypeCode = Codes.getNoteRecipientCodes().find(
              (o: any) => o.code === note.recipientType
            );
            const recipientTypeDisplay = recipientTypeCode
              ? recipientTypeCode.value
              : 'Unknown';

            let sitesDisplay = '';
            if (
              note.selectedClientSites &&
              note.selectedClientSites.length > 0
            ) {
              sitesDisplay = note.selectedClientSites
                .map((s: any) => s.siteName)
                .join(', ');
            }

            let resizedImageUrl = '';
            if (
              note.resizedImageList &&
              note.resizedImageList.length > 0 &&
              note.resizedImageList[0].signedURL
            ) {
              resizedImageUrl = note.resizedImageList[0].signedURL;
            }

            return {
              id: note.id,
              displayId: note.displayId,
              recipientType: note.recipientType,
              recipientTypeDisplay,
              npsdClient: note.npsdClient,
              npsdClientName: note.npsdClientName || '',
              selectedClientSites: note.selectedClientSites || [],
              sitesDisplay,
              dateTime: note.dateTime,
              loggedBy: note.loggedBy,
              loggedByName: note.loggedByName || '',
              onBehalfOf: note.onBehalfOf,
              onBehalfOfName: note.onBehalfOfName || '',
              location: note.location || '',
              subject: note.subject || '',
              notes: note.notes || '',
              attachmentList: note.attachmentList || [],
              resizedImageList: note.resizedImageList || [],
              resizedImageUrl,
            };
          });

          setNotes(mappedNotes);
          setTotalCount(newTotalCount);
          setTotalPages(newTotalPages);
          setNotesGridRows({ entries: mappedNotes });
          setNotesGridPager({
            curPage: page,
            numPages: newTotalPages,
            records: newTotalCount,
            isVisible: newTotalPages > 1,
          });
          setIsLoading(false);
          flushNoteWriteTelemetry();
        }
      );
    },
    [] // stable - uses refs
  );

  const loadClientSites = useCallback((clientId: number | null) => {
    if (!clientId) {
      setClientSites([]);
      return;
    }
    api.listClientGroupSite(
      clientId,
      (result: any) => {
        setClientSites(result || []);
      },
      () => {
        setClientSites([]);
      }
    );
  }, []);

  // ===== Refresh =====

  const refreshNotesPage = useCallback(() => {
    loadNotes();
    if (activeTabRef.current === 'auditLog') {
      auditLogRef.current?.refresh();
    }
  }, [loadNotes]);

  // Register refresh callback
  useEffect(() => {
    refreshCallback = refreshNotesPage;
    return () => {
      refreshCallback = null;
    };
  }, [refreshNotesPage]);

  // Start/stop page refresh interval (handles both registry and legacy mount paths)
  useEffect(() => {
    resetNotesPageRefresh();
    return () => {
      clearNotesPageRefresh();
    };
  }, []);

  // ===== Image handler (exposed on window) =====

  const openImage = useCallback((note: NoteListItem) => {
    if (note.attachmentList && note.attachmentList.length > 0) {
      const attachment = note.attachmentList[0];
      if (attachment.signedURL) {
        openWebpage(attachment.signedURL);
      }
    }
  }, []);

  // Expose instance methods for grid button callbacks
  useEffect(() => {
    (window as any).notesVueInstance = { openImage };
    return () => {
      (window as any).notesVueInstance = null;
    };
  }, [openImage]);

  // ===== Initial Data Load =====

  useEffect(() => {
    // Load dropdown data
    setRecipientTypes(Codes.getNoteRecipientCodes() || []);

    api.listClientGroup((result: any) => {
      if (result && result.npsdClientList) {
        setClients(
          result.npsdClientList.sort((a: any, b: any) =>
            a.name > b.name ? 1 : -1
          )
        );
      }
    });

    if (
      currentUserHasPermission(
        AccessAuthorities.ROLE_ORNA_NOTES_ON_BEHALF_OF
      )
    ) {
      api.getNoteOnBehalfOfUsers((result: any) => {
        if (result && result.people) {
          setOnBehalfOfUsers(
            result.people.sort((a: any, b: any) =>
              a.name > b.name ? 1 : -1
            )
          );
        }
      });
    }

    loadNotes();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ===== Filter Handlers =====

  const toggleFilters = useCallback(() => {
    setFiltersExpanded((prev) => !prev);
  }, []);

  const searchNotes = useCallback(() => {
    setCurrentPage(1);
    currentPageRef.current = 1;
    loadNotes(1);
  }, [loadNotes]);

  const clearFilters = useCallback(() => {
    setFilters({ subject: '', location: '', notes: '' });
    filtersRef.current = { subject: '', location: '', notes: '' };
    setCurrentPage(1);
    currentPageRef.current = 1;
    loadNotes(1);
  }, [loadNotes]);

  const updateFilter = useCallback(
    (field: keyof NotesFilters, value: string) => {
      setFilters((prev) => {
        const next = { ...prev, [field]: value };
        filtersRef.current = next;
        return next;
      });
    },
    []
  );

  // ===== Pagination =====

  const notesGoToPage = useCallback(
    (page: number) => {
      if (page < 1 || page > totalPages) return;
      setCurrentPage(page);
      currentPageRef.current = page;
      loadNotes(page);
    },
    [totalPages, loadNotes]
  );

  // ===== Row Actions =====

  const onNoteRowAction = useCallback(
    (payload: { action: string; row: any }) => {
      const { action, row } = payload;
      if (action === 'edit') {
        openModifyNoteModal(row);
      } else if (action === 'delete') {
        confirmDeleteNote(row);
      }
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ===== Note Modal =====

  const openAddNoteModal = useCallback(() => {
    const newNote = createEmptyEditingNote();
    newNote.dateTime = new Date();
    const types = Codes.getNoteRecipientCodes() || [];
    if (types.length > 0) {
      newNote.recipientType = types[0].code;
    }

    setIsNewNote(true);
    setEditingNote(newNote);
    setClientSites([]);
    setIsDateTimePickerOpen(false);
    setShowNoteModal(true);
  }, []);

  const openModifyNoteModal = useCallback(
    (note: NoteListItem) => {
      const modified: EditingNote = {
        id: note.id,
        displayId: note.displayId,
        recipientType: note.recipientType,
        npsdClient: note.npsdClient,
        selectedSiteIds: (note.selectedClientSites || []).map(
          (s: any) => s.id
        ),
        dateTime: note.dateTime ? new Date(note.dateTime) : new Date(),
        onBehalfOf: note.onBehalfOf,
        location: note.location,
        subject: note.subject,
        notes: note.notes,
        attachmentList: deepCopyProperties(note.attachmentList || []),
        resizedImageList: deepCopyProperties(note.resizedImageList || []),
        attachmentInfoList: getAttachmentInfoList(note.attachmentList || []),
      };

      setIsNewNote(false);
      setEditingNote(modified);

      if (note.npsdClient) {
        loadClientSites(note.npsdClient);
      } else {
        setClientSites([]);
      }

      setIsDateTimePickerOpen(false);
      setShowNoteModal(true);
    },
    [loadClientSites]
  );

  const closeNoteModal = useCallback(() => {
    setShowNoteModal(false);
    setIsDateTimePickerOpen(false);
    setEditingNote(createEmptyEditingNote());
  }, []);

  // ===== Note Modal Field Handlers =====

  const onRecipientTypeChange = useCallback(
    (value: string | number | null) => {
      setEditingNote((prev) => {
        const updated = { ...prev, recipientType: value as string };
        if (value !== 'c') {
          updated.npsdClient = null;
          updated.selectedSiteIds = [];
        }
        return updated;
      });
      if (value !== 'c') {
        setClientSites([]);
      }
    },
    []
  );

  const onClientChange = useCallback(
    (value: string | number | null) => {
      const clientId = value as number | null;
      setEditingNote((prev) => ({
        ...prev,
        npsdClient: clientId,
        selectedSiteIds: [],
      }));
      if (clientId) {
        loadClientSites(clientId);
      } else {
        setClientSites([]);
      }
    },
    [loadClientSites]
  );

  const onSiteIdsChange = useCallback((values: any[]) => {
    setEditingNote((prev) => ({
      ...prev,
      selectedSiteIds: values,
    }));
  }, []);

  const onOnBehalfOfChange = useCallback(
    (value: string | number | null) => {
      setEditingNote((prev) => ({
        ...prev,
        onBehalfOf: value as number | null,
      }));
    },
    []
  );

  const onDateTimeChange = useCallback((value: Date | null) => {
    if (value) {
      setEditingNote((prev) => ({ ...prev, dateTime: value }));
    }
  }, []);

  const openDateTimePicker = useCallback(() => {
    setTimeout(() => {
      setIsDateTimePickerOpen(true);
    }, 150);
  }, []);

  const closeDateTimePicker = useCallback(() => {
    setIsDateTimePickerOpen(false);
  }, []);

  // ===== Attachment helpers =====

  const getResizedImageUrl = useCallback(
    (attachmentId: number): string => {
      const resizedImage = getObjectInArrayByProperty(
        editingNoteRef.current.resizedImageList,
        'id',
        attachmentId
      );
      return resizedImage ? resizedImage.signedURL : '';
    },
    []
  );

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const fileInput = event.target;
      const fileList = fileInput.files;
      if (!fileList || fileList.length === 0) return;

      const files: File[] = Array.from(fileList);
      fileInput.value = '';

      uploadAttachments(files);
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const uploadAttachments = useCallback((files: File[]) => {
    const attachmentList: any[] = [];

    for (const file of files) {
      const attachment: any = {
        id: null,
        signedURL: '',
        filename: file.name,
        title: '',
        typeName: null,
        createdBy: '',
        dateCreated: '',
      };
      const lastDotIndex = file.name.lastIndexOf('.');
      attachment.title =
        lastDotIndex > 0 ? file.name.substring(0, lastDotIndex) : file.name;
      attachmentList.push(attachment);
    }

    setIsUploadingAttachment(true);
    App.uploadStarted();
    App.uploadProgress(1);
    App.initBlobUploadProgressControl();

    const startedAt = Date.now();
    api.saveAttachment(attachmentList, (result: any) => {
      App.uploadProgress(1);

      // Stay in the uploading state until every file has finished, not just the first.
      let remainingUploads = result.azureStorageBlobInfoList.length;
      const finishOneUpload = () => {
        remainingUploads--;
        if (remainingUploads <= 0) {
          setIsUploadingAttachment(false);
        }
      };
      if (remainingUploads === 0) {
        App.uploadCompleted();
        setIsUploadingAttachment(false);
      }

      for (let i = 0; i < result.azureStorageBlobInfoList.length; i++) {
        load.showLoadingScreen();
        App.blobUploadStarted();

        const file = files[i];
        const attachmentInfo: any = {
          id: null,
          activationKey: '',
        };
        const blobInfo = result.azureStorageBlobInfoList[i];

        attachmentInfo.id = blobInfo.attachmentId;
        attachmentInfo.activationKey = blobInfo.activationKey;

        const sasReadToken: string = blobInfo.sasReadToken;
        const sasWriteToken: string = blobInfo.sasWriteToken;
        const host: string = blobInfo.host;
        const containerName: string = blobInfo.containerName;
        const blobName: string = blobInfo.blobName;
        const attachment = attachmentList[i];

        attachment.id = blobInfo.attachmentId;
        attachment.signedURL = `${host}/${containerName}/${blobName}?${sasReadToken}`;

        // Update editingNote with new attachment info and attachment
        setEditingNote((prev) => ({
          ...prev,
          attachmentInfoList: [...prev.attachmentInfoList, attachmentInfo],
          attachmentList: [...prev.attachmentList, attachment],
        }));

        const customBlockSize =
          file.size > 1024 * 1024 * 32
            ? 1024 * 1024 * 4
            : 1024 * 512;

        uploadBlockBlob(host, sasWriteToken, containerName, blobName, file, {
          blockSize: customBlockSize,
          onProgress: (percent) => {
            App.setBlobUploadProgress(percent);
            App.uploadProgress(percent);
          },
        }).then(() => {
          App.uploadCompleted();
          App.showPopupNotification('File uploaded', 'success');
          setEditingNote((prev) => ({
            ...prev,
            resizedImageList: [
              ...prev.resizedImageList,
              attachment,
            ],
          }));
          App.blobUploadCompleted();
          load.hideLoadingScreen();
          finishOneUpload();
        }).catch(() => {
          App.uploadCompleted();
          App.showPopupNotification('File failed to upload', 'error');
          App.blobUploadCompleted();
          load.hideLoadingScreen();
          finishOneUpload();
        });
      }
    }, (result: any, failure?: AjaxFailureInfo) => {
      App.uploadCompleted();
      setIsUploadingAttachment(false);
      recordNoteWriteFailure('attachment', result, failure, startedAt);
    });
  }, []);

  const removeAttachment = useCallback((attachmentId: number) => {
    setEditingNote((prev) => ({
      ...prev,
      resizedImageList: prev.resizedImageList.filter(
        (item: any) => item.id !== attachmentId
      ),
      attachmentList: prev.attachmentList.filter(
        (item: any) => item.id !== attachmentId
      ),
      attachmentInfoList: prev.attachmentInfoList.filter(
        (item: any) => item.id !== attachmentId
      ),
    }));
  }, []);

  // ===== Save Note =====

  const saveNote = useCallback(() => {
    const note = editingNoteRef.current;

    // Validation
    if (!note.recipientType) {
      App.showPopupNotification('Please select a recipient type.', 'error');
      return;
    }
    if (note.recipientType === 'c' && !note.npsdClient) {
      App.showPopupNotification('Please select a client.', 'error');
      return;
    }
    if (!note.subject || !note.subject.trim()) {
      App.showPopupNotification('Please enter a subject.', 'error');
      return;
    }
    if (!note.notes || !note.notes.trim()) {
      App.showPopupNotification('Please enter notes.', 'error');
      return;
    }

    setIsSaving(true);

    const dateTime =
      moment(note.dateTime).utc().format('YYYY-MM-DDTHH:mm:ss') + '.000Z';

    let client = null;
    if (note.recipientType === 'c' && note.npsdClient) {
      client = {
        id: note.npsdClient,
        sites: note.selectedSiteIds || [],
      };
    }

    const noteData = {
      recipientType: note.recipientType,
      dateTime: dateTime,
      client: client,
      onBehalfOf: note.onBehalfOf,
      location: note.location || '',
      subject: note.subject,
      notes: note.notes,
      attachmentInfoList: note.attachmentInfoList || [],
    };

    // On failure keep the dialog open with everything the guard entered, so they can retry.
    // HTTP errors are already shown by ajaxCall; a lost connection needs its own message.
    const startedAt = Date.now();
    const onSaveFailed = (action: 'save' | 'update') =>
      (result: any, failure?: AjaxFailureInfo) => {
        setIsSaving(false);
        recordNoteWriteFailure(action, result, failure, startedAt);
        if (failure?.networkError) {
          App.showPopupNotification(
            action === 'save'
              ? 'The note could not be saved because the connection was interrupted. Your entry is still here; please try again.'
              : 'The changes could not be saved because the connection was interrupted. Your entry is still here; please try again.',
            'error'
          );
        }
      };

    if (note.id) {
      // Update existing note
      api.updateNote(note.id, noteData, () => {
        setIsSaving(false);
        App.showPopupNotification('Note updated successfully.', 'success');
        closeNoteModal();
        loadNotes();
      }, onSaveFailed('update'));
    } else {
      // Create new note
      api.saveNote(noteData, () => {
        setIsSaving(false);
        App.showPopupNotification('Note saved successfully.', 'success');
        closeNoteModal();
        loadNotes();
      }, onSaveFailed('save'));
    }
  }, [closeNoteModal, loadNotes]);

  // ===== Delete Note =====

  const confirmDeleteNote = useCallback((note: NoteListItem) => {
    setNoteToDelete(note);
    setShowDeleteModal(true);
  }, []);

  const deleteNote = useCallback(() => {
    if (!noteToDelete) return;

    setIsDeletingNote(true);
    const startedAt = Date.now();
    api.deleteNote(noteToDelete.id, () => {
      setIsDeletingNote(false);
      App.showPopupNotification('Note deleted successfully.', 'success');
      setShowDeleteModal(false);
      setNoteToDelete(null);
      loadNotes();
    }, (result: any, failure?: AjaxFailureInfo) => {
      setIsDeletingNote(false);
      recordNoteWriteFailure('delete', result, failure, startedAt);
    });
  }, [noteToDelete, loadNotes]);

  const cancelDelete = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  // ===== Render =====

  return (
    <div className={`notes-screen ${theme}`.trim()}>
      {/* Header */}
      <div className="header">
        <h1>Notes</h1>
        <div className="header-actions">
          {canCreateNote && (
            <button className="btn-new" onClick={openAddNoteModal}>
              <i className="fa fa-plus"></i> New Note
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="notes-tabs">
        <button
          className={`notes-tab${activeTab === 'notes' ? ' active' : ''}`}
          onClick={() => setActiveTab('notes')}
        >
          Notes
        </button>
        <button
          className={`notes-tab${activeTab === 'auditLog' ? ' active' : ''}`}
          onClick={() => setActiveTab('auditLog')}
        >
          Audit Log
        </button>
      </div>

      {/* Notes Tab Content */}
      <div style={{ display: activeTab === 'notes' ? undefined : 'none' }}>
        <NotesFilters
          filters={filters}
          filtersExpanded={filtersExpanded}
          activeFilterCount={activeFilterCount}
          onToggleFilters={toggleFilters}
          onSearch={searchNotes}
          onClear={clearFilters}
          onUpdateFilter={updateFilter}
        />

        {/* Notes Grid */}
        <div className="kst-results-panel">
          <div className="kst-results-body">
            <KstDataGrid
              columns={notesGridColumns}
              rows={notesGridRows}
              pager={notesGridPager}
              rowactions={notesRowActions}
              showcheckbox={false}
              showheader={false}
              mobiletitlefield="subject"
              expandable={true}
              mobilekeycolumns={[
                'dateTime',
                'loggedByName',
                'recipientTypeDisplay',
                'npsdClientName',
                'notes',
              ]}
              loading={isLoading}
              onGoToPage={notesGoToPage}
              onRowAction={onNoteRowAction}
            />
          </div>
        </div>
      </div>

      {/* Audit Log Tab Content */}
      <NotesAuditLogTab ref={auditLogRef} isVisible={activeTab === 'auditLog'} />

      {/* Add/Modify Note Modal */}
      <NoteFormModal
        open={showNoteModal}
        isNewNote={isNewNote}
        isSaving={isSaving}
        isUploadingAttachment={isUploadingAttachment}
        editingNote={editingNote}
        recipientTypeItems={recipientTypeItems}
        clientItems={clientItems}
        clientSites={clientSites}
        onBehalfOfItems={onBehalfOfItems}
        canSetOnBehalfOf={canSetOnBehalfOf}
        isDateTimePickerOpen={isDateTimePickerOpen}
        theme={theme}
        onSave={saveNote}
        onCancel={closeNoteModal}
        onRecipientTypeChange={onRecipientTypeChange}
        onClientChange={onClientChange}
        onSiteIdsChange={onSiteIdsChange}
        onOnBehalfOfChange={onOnBehalfOfChange}
        onDateTimeChange={onDateTimeChange}
        onOpenDateTimePicker={openDateTimePicker}
        onCloseDateTimePicker={closeDateTimePicker}
        onDateTimePickerOpenChange={(open) => setIsDateTimePickerOpen(open)}
        onEditingNoteChange={setEditingNote}
        onFileSelect={handleFileSelect}
        onRemoveAttachment={removeAttachment}
        getResizedImageUrl={getResizedImageUrl}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={showDeleteModal}
        title="Delete Note"
        messageHtml={deleteConfirmMessage}
        confirmText="Delete"
        cancelText="Cancel"
        confirmClass="danger"
        confirmDisabled={isDeletingNote}
        busy={isDeletingNote}
        theme={theme}
        onConfirm={deleteNote}
        onCancel={cancelDelete}
      />
    </div>
  );
}

// ============================================================================
// Imperative Destroy Function
// ============================================================================

function destroyNotesScreen(): void {
  clearNotesPageRefresh();
  if (notesContainer) {
    destroyReactComponent(notesContainer);
    // Replace with empty placeholder
    const parent = notesContainer.parentNode;
    if (parent) {
      const placeholder = document.createElement('div');
      placeholder.id = 'notesScreen';
      parent.replaceChild(placeholder, notesContainer);
    }
    notesContainer = null;
  }
}

// ============================================================================
// Module-level Page Refresh Interval Management
// ============================================================================

function clearNotesPageRefresh(): void {
  if (notesPageRefreshInterval) {
    clearInterval(notesPageRefreshInterval);
    notesPageRefreshInterval = null;
  }
}

function resetNotesPageRefresh(): void {
  clearNotesPageRefresh();
  notesPageRefreshInterval = setInterval(() => {
    if (refreshCallback) {
      refreshCallback();
    }
  }, 60000);
}

function refreshNotesPage(): void {
  if (refreshCallback) {
    refreshCallback();
  }
}

// ============================================================================
// Window Registration
// ============================================================================

(window as any).NotesScreen = {
  cleanUpNotesScreen: destroyNotesScreen,
  refreshNotesPage,
  resetNotesPageRefresh,
  clearNotesPageRefresh,
};

(window as any).clearNotesPageRefresh = clearNotesPageRefresh;
(window as any).resetNotesPageRefresh = resetNotesPageRefresh;

// ============================================================================
// Screen Registry
// ============================================================================

// ============================================================================
// Exports
// ============================================================================

export {
  destroyNotesScreen,
  clearNotesPageRefresh,
  resetNotesPageRefresh,
  refreshNotesPage,
};
